// SAVE AS: src/pages/admin/MembershipPlanManagement.tsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Search,
  Refresh,
  Edit,
  Close,
  WorkspacePremium,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';
import {
  useListPlansQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  MemberPlan,
  PlanFormData,
} from '../../features/admin/planApi.ts';

// ─── Empty form ───────────────────────────────────────────────────────────────
const EMPTY_FORM: PlanFormData = {
  plan_name: '',
  plan_price: '',
  stripe_plan_id: '',
  description: '',
  recurring: 1,
  status: 1,
};

// ─── Component ────────────────────────────────────────────────────────────────
const MembershipPlanManagement = () => {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen,     setMobileOpen]     = useState(false);
  const [openAddDialog,  setOpenAddDialog]  = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedPlan,   setSelectedPlan]   = useState<MemberPlan | null>(null);
  const [formData,       setFormData]       = useState<PlanFormData>(EMPTY_FORM);
  const [formErrors,     setFormErrors]     = useState<Partial<Record<keyof PlanFormData, string>>>({});
  const [searchTerm,     setSearchTerm]     = useState('');
  const [snackbar,       setSnackbar]       = useState({
    open: false, message: '', severity: 'success' as 'success' | 'error',
  });

  // ── API ──────────────────────────────────────────────────────────────────────
  const { data, isLoading, isError, refetch } = useListPlansQuery();
  const [createPlan, { isLoading: isCreating }] = useCreatePlanMutation();
  const [updatePlan, { isLoading: isUpdating }] = useUpdatePlanMutation();

  const plans: MemberPlan[] = data?.data ?? [];

  // ── Filtered list ────────────────────────────────────────────────────────────
  const filteredPlans = plans.filter(p =>
    p.plan_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.stripe_plan_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const handleDrawerToggle = () => setMobileOpen(o => !o);

  const handleRefresh = () => {
    refetch();
    setSnackbar({ open: true, message: 'Plans refreshed successfully!', severity: 'success' });
  };

  const showSnack = (message: string, severity: 'success' | 'error') =>
    setSnackbar({ open: true, message, severity });

  // ── Open Add ─────────────────────────────────────────────────────────────────
  const handleOpenAdd = () => {
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setOpenAddDialog(true);
  };
  const handleCloseAdd = () => {
    setOpenAddDialog(false);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  };

  // ── Open Edit ────────────────────────────────────────────────────────────────
  const handleOpenEdit = (plan: MemberPlan) => {
    setSelectedPlan(plan);
    setFormData({
      plan_name:      plan.plan_name,
      plan_price:     plan.plan_price,
      stripe_plan_id: plan.stripe_plan_id,
      description:    plan.description ?? '',
      recurring:      plan.recurring,
      status:         plan.status,
    });
    setFormErrors({});
    setOpenEditDialog(true);
  };
  const handleCloseEdit = () => {
    setOpenEditDialog(false);
    setSelectedPlan(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  };

  // ── Field change ─────────────────────────────────────────────────────────────
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof PlanFormData]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<number>, field: keyof PlanFormData) => {
    setFormData(prev => ({ ...prev, [field]: Number(e.target.value) }));
  };

  // ── Validation ───────────────────────────────────────────────────────────────
  const validateForm = (): boolean => {
    const errs: Partial<Record<keyof PlanFormData, string>> = {};
    if (!String(formData.plan_name).trim())
      errs.plan_name = 'Plan name is required';
    if (!formData.plan_price || Number(formData.plan_price) <= 0)
      errs.plan_price = 'Enter a valid price greater than 0';
    if (!String(formData.stripe_plan_id).trim())
      errs.stripe_plan_id = 'Stripe Plan ID is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Create ───────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!validateForm()) return;
    try {
      const payload = { ...formData, plan_price: Number(formData.plan_price) };
      const res = await createPlan(payload).unwrap();
      if (res.error) { showSnack(res.message, 'error'); return; }
      showSnack('Membership plan created successfully!', 'success');
      handleCloseAdd();
    } catch {
      showSnack('Error creating plan. Please try again.', 'error');
    }
  };

  // ── Update ───────────────────────────────────────────────────────────────────
  const handleUpdate = async () => {
    if (!validateForm() || !selectedPlan) return;
    try {
      const payload = { id: selectedPlan.id, ...formData, plan_price: Number(formData.plan_price) };
      const res = await updatePlan(payload).unwrap();
      if (res.error) { showSnack(res.message, 'error'); return; }
      showSnack('Membership plan updated successfully!', 'success');
      handleCloseEdit();
    } catch {
      showSnack('Error updating plan. Please try again.', 'error');
    }
  };

  // ── Status chip style (same helper pattern as coupon) ────────────────────────
  const getStatusStyle = (status: 1 | 2) =>
    status === 1
      ? { bgcolor: '#d1f4e0', color: '#00783e', fontWeight: 600 }
      : { bgcolor: '#e4e5e7', color: '#616161', fontWeight: 600 };

  const getRecurringStyle = (recurring: 1 | 2) =>
    recurring === 1
      ? { bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }
      : { bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600 };

  // ── Shared form fields (used in both Add and Edit dialogs) ───────────────────
  const renderFormFields = () => (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        label="Plan Name *"
        name="plan_name"
        value={formData.plan_name}
        onChange={handleInputChange}
        error={!!formErrors.plan_name}
        helperText={formErrors.plan_name}
        fullWidth
        placeholder="e.g. Gold, Platinum, Silver"
      />

      <TextField
        label="Plan Price (AED) *"
        name="plan_price"
        type="number"
        value={formData.plan_price}
        onChange={handleInputChange}
        error={!!formErrors.plan_price}
        helperText={formErrors.plan_price}
        fullWidth
        inputProps={{ min: 0, step: '0.01' }}
        placeholder="e.g. 79.00"
        InputProps={{
          startAdornment: <InputAdornment position="start">AED</InputAdornment>,
        }}
      />

      <TextField
        label="Stripe Plan ID *"
        name="stripe_plan_id"
        value={formData.stripe_plan_id}
        onChange={handleInputChange}
        error={!!formErrors.stripe_plan_id}
        helperText={formErrors.stripe_plan_id || 'Copy from Stripe Dashboard → Products → Price ID'}
        fullWidth
        placeholder="price_1SMiCyACqckcaAMBeqwZZhjG"
        inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
      />

      <TextField
        label="Description / Benefits"
        name="description"
        value={formData.description}
        onChange={handleInputChange}
        fullWidth
        multiline
        rows={2}
        placeholder="e.g. 10% off sitewide, Free express shipping"
      />

      <FormControl fullWidth>
        <InputLabel>Billing Cycle</InputLabel>
        <Select
          value={formData.recurring}
          label="Billing Cycle"
          onChange={(e) => handleSelectChange(e as SelectChangeEvent<number>, 'recurring')}
        >
          <MenuItem value={1}>Monthly (30 days)</MenuItem>
          <MenuItem value={2}>Yearly (365 days)</MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <InputLabel>Status *</InputLabel>
        <Select
          value={formData.status}
          label="Status *"
          onChange={(e) => handleSelectChange(e as SelectChangeEvent<number>, 'status')}
        >
          <MenuItem value={1}>Active</MenuItem>
          <MenuItem value={2}>Inactive</MenuItem>
        </Select>
      </FormControl>
    </Stack>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Sidebar ── */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 250 } }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* ── Main content ── */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />

          <Box sx={{ flexGrow: 1, overflow: 'auto', backgroundColor: '#f1f2f4' }}>
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>

              {/* ── Header row ── */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Membership Plan Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': { borderColor: '#202223', bgcolor: 'transparent' },
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenAdd}
                    sx={{
                      bgcolor: '#008060',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#006e52' },
                    }}
                  >
                    Add New Plan
                  </Button>
                </Box>
              </Box>

              {/* ── Card + Table ── */}
              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>

                {/* Search bar */}
                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    placeholder="Search plans..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                      minWidth: 300,
                      '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white' },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                {/* Loading */}
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {/* Error */}
                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">
                      Failed to load membership plans. Please try again.
                      <Button color="inherit" size="small" onClick={() => refetch()} sx={{ ml: 1 }}>
                        Retry
                      </Button>
                    </Alert>
                  </Box>
                )}

                {/* Empty */}
                {!isLoading && !isError && filteredPlans.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <WorkspacePremium sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No membership plans found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Get started by creating your first membership plan
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleOpenAdd}
                      sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#006e52' } }}
                    >
                      Add New Plan
                    </Button>
                  </Box>
                )}

                {/* Table */}
                {!isLoading && filteredPlans.length > 0 && (
                  <Box sx={{ bgcolor: 'white' }}>

                    {/* Desktop Table */}
                    {!isMobile && (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#fafbfb' }}>
                              {['#', 'Plan Name', 'Price (AED)', 'Stripe Plan ID', 'Billing', 'Description', 'Status', 'Actions'].map(h => (
                                <TableCell key={h} sx={{ fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', color: '#6d7175' }}>
                                  {h}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredPlans.map((plan, idx) => (
                              <TableRow
                                key={plan.id}
                                sx={{ '&:hover': { bgcolor: '#f9fafb' }, transition: 'background-color 0.15s ease' }}
                              >
                                <TableCell sx={{ color: '#6d7175', fontSize: '13px' }}>{idx + 1}</TableCell>

                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                    {plan.plan_name}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#008060' }}>
                                    AED {Number(plan.plan_price).toFixed(2)}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'monospace', fontSize: '12px', color: '#6d7175', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    {plan.stripe_plan_id}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Chip
                                    label={plan.recurring === 1 ? 'Monthly' : 'Yearly'}
                                    size="small"
                                    sx={{ fontSize: '12px', ...getRecurringStyle(plan.recurring) }}
                                  />
                                </TableCell>

                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: '#6d7175', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    {plan.description || '—'}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Chip
                                    label={plan.status === 1 ? 'Active' : 'Inactive'}
                                    size="small"
                                    sx={{ fontSize: '12px', ...getStatusStyle(plan.status) }}
                                  />
                                </TableCell>

                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenEdit(plan)}
                                    sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    {/* Mobile Card View */}
                    {isMobile && (
                      <Box sx={{ p: 2 }}>
                        {filteredPlans.map((plan, idx) => (
                          <Card key={plan.id} sx={{ mb: 2, p: 2, borderRadius: '12px', border: '1px solid #e1e3e5' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                {plan.plan_name}
                              </Typography>
                              <Chip label={plan.status === 1 ? 'Active' : 'Inactive'} size="small"
                                sx={{ fontSize: '10px', ...getStatusStyle(plan.status) }} />
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Price</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#008060' }}>
                                  AED {Number(plan.plan_price).toFixed(2)}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" color="text.secondary">Billing</Typography>
                                <Typography variant="body2">
                                  {plan.recurring === 1 ? 'Monthly' : 'Yearly'}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', display: 'block', mb: 1 }}>
                              {plan.stripe_plan_id}
                            </Typography>
                            {plan.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                {plan.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <IconButton size="small" onClick={() => handleOpenEdit(plan)}
                                sx={{ color: '#6d7175', '&:hover': { bgcolor: '#f1f2f4' } }}>
                                <Edit fontSize="small" />
                              </IconButton>
                            </Box>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ══ Add Plan Dialog ══════════════════════════════════════════════════════ */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAdd}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
              Add New Membership Plan
            </Typography>
            <IconButton onClick={handleCloseAdd} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>{renderFormFields()}</DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseAdd}
            disabled={isCreating}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#f44336' }}
          >
            Close
          </Button>
          <Button
            onClick={handleCloseAdd}
            disabled={isCreating}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#F78E12' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={isCreating}
            sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#006e52' } }}
          >
            {isCreating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Creating...
              </Box>
            ) : 'Create Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Edit Plan Dialog ═════════════════════════════════════════════════════ */}
      <Dialog
        open={openEditDialog}
        onClose={handleCloseEdit}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
              Edit Membership Plan
            </Typography>
            <IconButton onClick={handleCloseEdit} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>{renderFormFields()}</DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseEdit}
            disabled={isUpdating}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#f44336' }}
          >
            Close
          </Button>
          <Button
            onClick={handleCloseEdit}
            disabled={isUpdating}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#eeeeee', bgcolor: '#F78E12' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={isUpdating}
            sx={{ bgcolor: '#008060', textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#006e52' } }}
          >
            {isUpdating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Updating...
              </Box>
            ) : 'Update Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ Snackbar ═════════════════════════════════════════════════════════════ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MembershipPlanManagement;